import React, { useEffect, useState } from 'react';
import { Box, TablePagination, Typography, useTheme } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GenericTable from '../common/GenericTable';
import { useNavigate } from 'react-router-dom';
import { orderService } from '../../services/orders';
import { useSelector } from 'react-redux';
import { statusMap } from '../../utils/statuses';

const OrderHistory = () => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [last_page, setLastPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  const [orderHistoryData, setOrderHistoryData] = useState([]);
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useSelector(state => state.auth);

  const getOrders = async (currentPage, currentRowsPerPage) => {
    try {
      setIsLoading(true);
      const response = await orderService.fetchOrderlist(user?.id, currentRowsPerPage, currentPage);
      if (response.status === 200) {
        setOrderHistoryData(response?.data?.data);
        setTotalRecords(response?.data?.meta?.total);
        setPage(response?.data?.meta?.current_page);
        setLastPage(response?.data?.meta?.last_page);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Sort handler function
  const handleSort = (columnKey) => {
    const direction = sortConfig.key === columnKey && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key: columnKey, direction });
    
    const sortedData = [...orderHistoryData].sort((a, b) => {
      if (a[columnKey] < b[columnKey]) return direction === 'asc' ? -1 : 1;
      if (a[columnKey] > b[columnKey]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setOrderHistoryData(sortedData);
  };

  const columns = [
    { label: "Order ID", field: "id",
      render: (id) => {
        return id ? `#${id}` : "N/A";
      },
     },
    { label: "Customer", field: "customer",
      render: (customer) => {
        return customer?.customer_business || "N/A";
      },
     },
     {
      label: "No of Products",
      field: "product_count",
      render: (row, product_count) => {
        return product_count?.items?.length || 0;
      },
    },    
    {
      label: "Status",
      field: "status",
      render: (statusId) => {
        return statusMap[statusId] || "Unknown Status";
      },
    },
    {
      label: "Total",
      field: "total",
      render: (total) => {
        if (typeof total === "number") {
          return `$${total.toFixed(2)}`;
        }
        return total ? `$${parseFloat(total).toFixed(2)}` : "N/A";
      },
    },
    { label: "Date Added", field: "order_date" },
  ];

  const actions = [
    {
      icon: <VisibilityIcon />,
      onClick: (row) => navigate(`/my/order-detail/${row?.id}`),
      style: { backgroundColor: theme.palette.primary.gray, minWidth: '40px', padding: '6px' }
    }
  ];

  const handlePageChange = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  useEffect(() => {
    if (user?.id) {
      getOrders(page, rowsPerPage);
    }
  }, [page, rowsPerPage, user?.id]);

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', height: 'auto', flexGrow: 1, gap: 1 }}>
      <Typography variant="h4" sx={{ color: theme.palette.heading_color.main, fontWeight: 600, fontSize: { xs: 20, sm: 24 } }}>
        Order History
      </Typography>
      <GenericTable 
        columns={columns} 
        data={orderHistoryData} 
        actions={actions} 
        onSort={handleSort} 
        sortConfig={sortConfig}
        renderActions={(row) => !row.isSummary}
        loading={isLoading}
      />
      <TablePagination
        component="div"
        count={totalRecords}
        page={page - 1} 
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Box>
  );
};

export default OrderHistory;